import Add from '@mui/icons-material/Add'
import { Box, Button, Card, CardContent, IconButton, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { DataControlCard, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'

import { HoldsAuditTrail } from '@planning/pages/Holds/organisms/HoldsAuditTrail'
import { HoldsFormContent } from '@planning/pages/Holds/organisms/HoldsFormContent'
import { OrderHoldsSectionStore } from './stores/OrderHoldsSectionStore'

interface Props {
  store: OrderHoldsSectionStore
  allowAdd?: boolean
  allowEdit?: boolean
  hideActions?: boolean
  containerNumber?: string
  onClickSave?: () => Promise<void>
}

export const OrderHoldsSection = observer(
  ({
    store,
    allowAdd = false,
    allowEdit = false,
    hideActions,
    containerNumber,
    onClickSave,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    if (allowEdit) {
      const isEmpty = store.holdsAuditTrail.length === 0

      return (
        <DataControlCard
          title={t('holds', 'Holds')}
          hideActions={hideActions}
          allowEdit
          onClickSave={onClickSave}
          viewComponent={
            isEmpty ? (
              <Typography variant='body2' color='text.secondary'>
                {t('noHoldAddedYet', 'No hold added yet')}
              </Typography>
            ) : (
              <HoldsAuditTrail store={store} containerNumber={containerNumber} />
            )
          }
          editComponent={
            <Stack gap={theme.customSpacing.m} width='100%'>
              <HoldsFormContent store={store} />
              <Box>
                <Button size='small' startIcon={<Add />} onClick={store.addHold}>
                  {t('addHold', 'Add hold')}
                </Button>
              </Box>
            </Stack>
          }
        />
      )
    }

    const isEmpty = store.holdsAuditTrail.length === 0

    if (isEmpty && !allowAdd) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: theme.customSpacing.s,
            borderRadius: theme.customRadius.small,
            bgcolor: theme.palette.background.neutral,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            {t('noHoldAddedYet', 'No hold added yet')}
          </Typography>
        </Box>
      )
    }

    return (
      <Card variant='outlined' sx={{ borderColor: theme.palette.grey[300], boxShadow: 'none' }}>
        <CardContent>
          <Stack gap={theme.customSpacing.m}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant='subtitle1'>{t('holds', 'Holds')}</Typography>
              {allowAdd && (
                <IconButton size='small' onClick={store.addHold}>
                  <Add fontSize='small' />
                </IconButton>
              )}
            </Box>
            {allowAdd ? (
              <HoldsFormContent store={store} />
            ) : (
              <HoldsAuditTrail store={store} containerNumber={containerNumber} />
            )}
          </Stack>
        </CardContent>
      </Card>
    )
  },
)
